import React, { useState } from 'react';
import { AppNav } from '../../components/layout/AppNav';
import { Button } from '../../components/ui/Button';
import { useToast } from '../../components/ui/Toast';
import { useAuth } from '../../hooks/useAuth';
import { Info } from 'lucide-react';
import api from '../../services/api';

const roleDisplay: Record<string, string> = {
  'reseller-admin': 'Admin',
  'reseller-agent': 'Agent',
};

export const MyAccountPage: React.FC = () => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState(user?.name ?? '');

  const initials = name.split(' ').slice(0, 2).map((w) => w[0]).join('').toUpperCase() || 'U';

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch('/users/me', { name });
      showToast('Profile updated', 'success');
      setEditing(false);
    } catch {
      showToast('Failed to update profile', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <AppNav title="My Account" subtitle="Manage your profile" />
      <main style={{ flex: 1, padding: '24px', overflowY: 'auto' }}>
        <div style={{ maxWidth: '560px', display: 'flex', flexDirection: 'column', gap: '16px' }}>

          <div className="page-title" style={{ marginBottom: '8px' }}>My Account</div>

          {/* Profile card */}
          <div className="card">
            <div style={{ padding: '24px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '16px' }}>
              <div style={{
                width: '64px', height: '64px', borderRadius: '50%',
                background: 'var(--p-light)', color: 'var(--p)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '22px', fontWeight: 700, flexShrink: 0,
              }}>
                {initials}
              </div>
              <div>
                <div style={{ fontSize: '18px', fontWeight: 700, color: 'var(--text-1)' }}>{name || user?.name || '—'}</div>
                <div style={{ fontSize: '13px', color: 'var(--text-2)', marginTop: '2px' }}>
                  {roleDisplay[user?.role ?? ''] ?? user?.role ?? '—'}
                </div>
              </div>
            </div>

            <div className="card-body">
              {editing ? (
                <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <div>
                    <label className="lbl">Full Name</label>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                      className="inp"
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <Button type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save Changes'}</Button>
                    <Button variant="ghost" type="button" onClick={() => { setEditing(false); setName(user?.name ?? ''); }}>Cancel</Button>
                  </div>
                </form>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
                  {[
                    { label: 'Full Name', value: user?.name ?? '—' },
                    { label: 'Email', value: user?.email ?? '—' },
                    { label: 'Role', value: roleDisplay[user?.role ?? ''] ?? user?.role ?? '—' },
                  ].map(({ label, value }) => (
                    <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid var(--border)' }}>
                      <span style={{ fontSize: '13px', color: 'var(--text-2)', fontWeight: 500 }}>{label}</span>
                      <span style={{ fontSize: '13.5px', color: 'var(--text-1)', fontWeight: 600 }}>{value}</span>
                    </div>
                  ))}
                  <div style={{ marginTop: '16px' }}>
                    <Button variant="ghost" size="sm" onClick={() => setEditing(true)}>Edit Profile</Button>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div style={{ background: 'var(--succ-bg)', border: '1px solid #BBF7D0', borderRadius: '10px', padding: '12px 16px', fontSize: '13px', color: '#15803D', display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
            <Info size={14} style={{ flexShrink: 0, marginTop: '1px' }} />
            To change your password or registered mobile, please contact your administrator.
          </div>
        </div>
      </main>
    </div>
  );
};
